import apiClient from '@/services/http/client'
import type {
  SubscriptionInfo,
  SubscriptionCheckoutResponse,
  SubscriptionCatalogProduct,
} from '@/types'

// ─── GET /api/v1/subscription ────────────────────────────────────────────────
// Devuelve el estado de la suscripción del studio activo (X-Studio-Id en header).
// 404 → el studio no tiene suscripción; se mapea a null en el store (status: 'none').

export const getSubscription = async (): Promise<SubscriptionInfo | null> => {
  try {
    const res = await apiClient.get<{ data: SubscriptionInfo }>('/api/v1/subscription')
    return res.data.data
  } catch (err: unknown) {
    if ((err as { response?: { status?: number } }).response?.status === 404) return null
    throw err
  }
}

// ─── GET /api/v1/subscription/products ───────────────────────────────────────
// Catálogo de planes activos con precios disponibles.
// Usado para renderizar la pantalla de selección/cambio de plan.

export const getSubscriptionProducts = async (): Promise<SubscriptionCatalogProduct[]> => {
  const res = await apiClient.get<{ data: SubscriptionCatalogProduct[] }>('/api/v1/subscription/products')
  return res.data.data
}

// ─── POST /api/v1/subscription/checkout ──────────────────────────────────────
// Crea una sesión de Stripe Hosted Checkout con el precio seleccionado.
//
// 409 → suscripción ya activa.
// 422 → precio no disponible o inválido.

export const createSubscriptionCheckout = async (
  subscriptionPriceId: number,
  successUrl: string,
  cancelUrl: string,
): Promise<SubscriptionCheckoutResponse> => {
  const res = await apiClient.post<SubscriptionCheckoutResponse>(
    '/api/v1/subscription/checkout',
    {
      subscription_price_id: subscriptionPriceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    },
  )
  return res.data
}

// ─── POST /api/v1/subscription/change-plan ───────────────────────────────────
// Cambia el precio activo (upgrade/downgrade) sin salir a Stripe.
//
// 404 → no hay suscripción.
// 409 → ya está en ese precio.
// 422 → precio no disponible.

export const changeSubscriptionPlan = async (
  subscriptionPriceId: number,
): Promise<SubscriptionInfo> => {
  const res = await apiClient.post<{ data: SubscriptionInfo }>(
    '/api/v1/subscription/change-plan',
    { subscription_price_id: subscriptionPriceId },
  )
  return res.data.data
}

// ─── POST /api/v1/subscription/reactivate ────────────────────────────────────
// Reactiva una suscripción con cancelación programada (cancel_at_period_end=true).
// Deja la suscripción activa sin fecha de cancelación.
//
// 404 → no hay suscripción.
// 409 → la suscripción no tiene cancelación programada.

export const reactivateSubscription = async (): Promise<SubscriptionInfo> => {
  const res = await apiClient.post<{ data: SubscriptionInfo }>('/api/v1/subscription/reactivate')
  return res.data.data
}

// ─── POST /api/v1/subscription/cancel ────────────────────────────────────────
// Programa la cancelación al final del periodo actual.
// El campo cancel_at_period_end e is_cancellation_scheduled quedan en true.
//
// 404 → no hay suscripción activa.
// 409 → estado incorrecto o cancelación ya programada.

export const cancelSubscription = async (): Promise<SubscriptionInfo> => {
  const res = await apiClient.post<{ data: SubscriptionInfo }>('/api/v1/subscription/cancel')
  return res.data.data
}
